import React, { useEffect, useMemo, useCallback, useReducer } from "react";
import { useParams, useHistory } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import {
  convertFromRaw,
  EditorState,
  ContentState,
  convertToRaw,
} from "draft-js";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import ReactModal from "react-modal";
import { stateToHTML } from "draft-js-export-html";
import moment from "moment-timezone";
import xor from 'lodash/xor';

import {
  preliminaryDomesticSales,
  preliminaryDomesticOps,
  preliminaryInternationalSales,
  preliminaryInternationalOps,
  finalSales,
  finalOps,
  updated,
  styles,
} from "./default-email-content";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";

import Dropzone from "../shared/dropzone";
import { LoadingButton } from "../shared/loading-button";

import ai from "../../helpers/axios";
import { isTripClosedForUser } from "../../helpers/is-trip-editable";
import PageHeader from "../shared/page-header";
import * as Sentry from "@sentry/react";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const OPS_EMAIL = "ops@evojets.com";
const OPS_EMAIL_NAME = "evoJets Ops";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const sendItineraryActionToButtonMap = {
  sendEmail: "isEmailLoading",
};

const emailComponents = {
  DropdownIndicator: null,
};

const createOption = (label) => ({
  label,
  value: label,
});

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const prepareTemplate = (templateName, template, data, signature) => {
  const variablesMap = ["firstName", "ownerEmail"];
  const replaceVariables = (text) => {
    variablesMap.forEach((variable) => {
      text = text.replace(`{{${variable}}}`, data[variable]);
    });

    return text;
  };

  const signatureHTML = htmlToDraft(signature || "");
  const signatureState = ContentState.createFromBlockArray(
    signatureHTML.contentBlocks,
    signatureHTML.entityMap
  );
  const signatureRaw = convertToRaw(signatureState);
  const signatureBlocks = templateName.includes('-ops') ? [] : signatureRaw?.blocks ?? [];

  return convertFromRaw({
    entityMap: template.entityMap,
    blocks: [
      ...template.blocks.map((block) => ({
        ...block,
        text: replaceVariables(block.text, data),
      })),
      {
        key: "hcnb1",
        text: "",
        type: "paragraph",
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
      ...(signatureBlocks || []),
    ],
  });
};

const areArraysEqual = (arr1 , arr2 ) => {
  const arr1values = arr1?.map(({value}) => value) ?? [];
  const arr2values = arr2?.map(({value}) => value) ?? [];

  return xor(arr1values, arr2values).length === 0;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      if (action.field === "template") {
        return {
          ...state,
          [action.field]: action.value,
          bodyContentStateEditor: EditorState.createWithContent(
            prepareTemplate(
              action.value.value,
              action.value.template,
              state.emailOptions || {},
              state.signature
            )
          ),
          bodyContentEditorUpdated: false,
        };
      }

      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }

    case "dataDownloaded": {
      const data = action?.payload?.data ?? {};
      const user = action?.payload?.user ?? {};

      const initialValues = {
        recordHTML: data.html,
        templateData: data.templateData,
        emailOptions: data.emailOptions,
        actionLog: data.actionLog || [],
        appUser: data.appUser || null,
        bodyContentStateEditor: EditorState.createWithContent(
          prepareTemplate(
            "preliminary-domestic-sales",
            preliminaryDomesticSales,
            data.emailOptions || {},
            state.signature
          )
        ),
        bodyContentEditorUpdated: false,
        sendAs: {
          label: `${user.name} <${user.email}>`,
          value: user.email,
          id: user.sub,
        },
        template: {
          label: "Preliminary - Domestic / Sales",
          value: "preliminary-domestic-sales",
        },
      };

      return {
        ...state,
        loading: false,
        ...initialValues,
        initialFormValues: initialValues,
      };
    }

    case "loadingChanged": {
      if (action.value === false) {
        return {
          ...state,
          isFormLoading: false,
          isEmailLoading: false,
        };
      } else if (action.field === "isFormLoading") {
        return {
          ...state,
          isFormLoading: true,
          isEmailLoading: false,
        };
      }

      return {
        ...state,
        isFormLoading: true,
        [action.field]: action.value,
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = {...state.unsavedChanges};

      if (!action.value) {
        delete updatedUnsavedChanges[action.field];
      } else {
        updatedUnsavedChanges[action.field] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges
      };
    }

    default:
      break;
  }
  return state;
};

const ItineraryPreviewContent = (props) => {
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const { tripId, itineraryId } = useParams();
  const history = useHistory();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdminOrAd = userRoles.some(function (role) {
    return "Super Admin" === role || "Account Director" === role;
  });

  const superAdmin = userRoles.some((role) => "Super Admin" === role);

  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;

  const initialValues = {
    emailOptions: {},
    actionLog: [],
    appUser: null,
    bodyContentStateEditor: EditorState.createEmpty(),
    bodyContentEditorUpdated: false,
    testEmails: [],
    attachments: [],
    sendAs: {
      label: `${user.name} <${user.email}>`,
      value: user.email,
      id: user.sub,
    },
    template: {
      label: "Preliminary - Domestic / Sales",
      value: "preliminary-domestic-sales",
    },
  };

  let initialState = {
    loading: false,
    contractHTML: null,
    teamMembers: [],
    testEmailInputValue: "",
    showChangeEmailTemplateModal: false,
    tmpTemplate: null,
    signature: user?.["https://new.evojets.com/userMetadata"]?.signature,
    isOps: user?.["https://new.evojets.com/appMetadata"]?.isOps,

    ...initialValues,

    isFormLoading: false,
    isEmailLoading: false,
    tripTag: "",
    tripOwnerEmail: "",
    tripVerified: false,
    tripLocked: false,

    initialFormValues: initialValues,
    unsavedChanges: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const readOnly = isTripClosedForUser({
    verified: state.tripVerified,
    locked: state.tripLocked,
    superAdmin,
    verificationAdmin,
  });

  const setUnsavedChanges = useCallback(({field, value}) => {
    dispatch({
      type: "unsavedChanges",
      field,
      value
    })
  }, []);

  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let tripResponse = await ai.auth(token).get(`/api/trips/${tripId}`).catch((error) => {
      console.error(error);
      Sentry.captureException(error);
    });

    if (tripResponse && tripResponse.data) {
      dispatch({
        type: "fieldChanged",
        field: "tripTag",
        value: tripResponse.data.tripTag,
      });
      dispatch({
        type: "fieldChanged",
        field: "tripOwnerEmail",
        value: tripResponse.data.owner.email,
      });
      dispatch({
        type: "fieldChanged",
        field: "tripVerified",
        value: !!tripResponse.data.verified,
      });
      dispatch({
        type: "fieldChanged",
        field: "tripLocked",
        value: !!tripResponse.data.lockState?.locked,
      });
    }

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/itineraries/${itineraryId}/preview`)
      .catch((error) => {
        setTimeout(() => {
          toast.error("Unauthorized!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);

        return console.error(error.response);
      });

    if (response) {
      dispatch({
        type: "dataDownloaded",
        payload: {
          data: response.data,
          user,
        },
      });
    }
  };

  const handlePDF = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/itineraries/${itineraryId}/pdf`, {
        responseType: "arraybuffer",
      })
      .catch((error) => {
        setTimeout(() => {
          toast.error("Unauthorized!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);

        return console.error(error.response);
      });

    if (response) {
      dispatch({
        type: "pdfDownloaded",
        payload: {
          data: response.data,
        },
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      let fileName = "[EVOJETS] Itinerary.pdf";

      if (state?.templateData?.fileName) {
        fileName = state?.templateData?.fileName;
      }

      let link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      getData();
    }
  };

  const getTeamMembers = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    let { data } = await ai.auth(token).get(`/api/users/getTeamMembers`);

    dispatch({
      type: "fieldChanged",
      field: "teamMembers",
      value: data,
    });
  };

  useEffect(() => {
    getData();
    getTeamMembers();
    document.title = "Itinerary Preview | evoJets HQ";
  }, []);

  const sendItinerary = async (action) => {
    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    const formData = new FormData();

    formData.append("sendAs", JSON.stringify(state.sendAs));
    formData.append("template", state.template?.value);
    formData.append(
      "bodyContent",
      stateToHTML(state.bodyContentStateEditor.getCurrentContent()).replace("<figure>", `<figure style="margin: 0; padding: 0;">`),
    );
    formData.append("action", action);

    if (action === "sendTestEmail") {
      const recipients = state.testEmails.map((item) => item.value);
      formData.append("recipients", JSON.stringify(recipients));
    }

    for (const file of state.attachments) {
      formData.append("attachments[]", file);
    }

    let response;

    dispatch({
      type: "loadingChanged",
      field: sendItineraryActionToButtonMap[action] ?? "isFormLoading",
      value: true,
    });

    try {
      response = await ai
        .auth(token)({
          method: "post",
          url: `/api/trips/${tripId}/itineraries/${itineraryId}/send`,
          data: formData,
        })
        .catch((error) => {
          if (action === "sendTestEmail") {
            toast.error(getApiErrorMessage(error, "Request failed."), toastOptions);
          } else {
            toast.error(
              "Some email addresses are repeated in the TO, CC, or BCC fields. Please make sure each email address is entered only once and appears in only one field (TO, CC, or BCC).",
              toastOptions,
            );
          }
        });

      if (response) {
        const { actionLog } = response.data;
        dispatch({
          type: "fieldChanged",
          field: "actionLog",
          value: actionLog,
        });

        toast.success(
          [
            action === "sendTestEmail" && "Test",
            "Itinerary",
            "Email Sent",
            "successfully!",
          ]
            .filter(Boolean)
            .join(" "),
          toastOptions
        );
      }
    } finally {
      dispatch({
        type: "loadingChanged",
        field: "isFormLoading",
        value: false,
      });
    }
  };

  const sendTestEmail = async (event) => {
    event.preventDefault();

    if (!state.testEmails.length) {
      return toast.error(
        "Please fill in test email addresses and retry.",
        toastOptions
      );
    }

    return sendItinerary("sendTestEmail");
  };

  const sendLiveEmail = async (event) => {
    event.preventDefault();

    if (state.isFormLoading) {
      return;
    }

    return sendItinerary("sendEmail");
  };

  const handleTestEmailChange = useCallback((newValue) => {
    setUnsavedChanges({
      field: 'testEmails',
      value: !areArraysEqual(newValue, state.initialFormValues.email),
    });

    dispatch({
      type: "fieldChanged",
      field: "testEmails",
      value: newValue || [],
    });
  }, [setUnsavedChanges, state.initialFormValues.email]);

  const handleTestEmailInputChange = useCallback(
    (inputValue, action) => {
      setUnsavedChanges({
        field: 'testEmailInputValue',
        value: !!inputValue,
      });

      dispatch({
        type: "fieldChanged",
        field: "testEmailInputValue",
        value: inputValue,
      });

      if (
        action.action === "menu-close" &&
        validateEmail(state.testEmailInputValue)
      ) {
        const newEmails = [...state.testEmails, createOption(state.testEmailInputValue)];

        setUnsavedChanges({
          field: 'testEmails',
          value: !areArraysEqual(newEmails, state.initialFormValues.email),
        });

        setUnsavedChanges({
          field: 'testEmailInputValue',
          value: false,
        });

        dispatch({
          type: "fieldChanged",
          field: "testEmails",
          value: newEmails,
        });

        dispatch({
          type: "fieldChanged",
          field: "testEmailInputValue",
          value: "",
        });
      }
    },
    [setUnsavedChanges, state.testEmailInputValue, state.testEmails, state.initialFormValues.email]
  );

  const handleTestEmailKeyDown = useCallback(
    (event) => {
      if (
        !state.testEmailInputValue ||
        !validateEmail(state.testEmailInputValue)
      ) {
        return;
      }

      switch (event.key) {
        case "Enter":
        case "Tab":
          const newEmails = [...state.testEmails, createOption(state.testEmailInputValue)];

          setUnsavedChanges({
            field: 'testEmails',
            value: !areArraysEqual(newEmails, state.initialFormValues.email),
          });

          setUnsavedChanges({
            field: 'testEmailInputValue',
            value: false,
          });

          dispatch({
            type: "fieldChanged",
            field: "testEmails",
            value: newEmails,
          });

          dispatch({
            type: "fieldChanged",
            field: "testEmailInputValue",
            value: "",
          });

          event.preventDefault();
      }
    },
    [state.testEmailInputValue, state.testEmails, state.initialFormValues.email, setUnsavedChanges]
  );

  const ccList = useMemo(() => {
    return [
      state.emailOptions?.ccOwner && state.tripOwnerEmail ? state.tripOwnerEmail : '',
      ...(state?.emailOptions?.cc || []),
    ]
      .filter(Boolean)
      .join(", ");
  }, [state?.emailOptions, state.tripOwnerEmail]);

  const bccList = useMemo(() => {
    return [
      state.emailOptions?.bccSender && `${state.sendAs.label}`,
      ...(state?.emailOptions?.bcc || []),
    ]
      .filter(Boolean)
      .join(", ");
  }, [state.sendAs, state?.emailOptions]);

  const senderOptions = useMemo(() => {
    const senderOptions = [
      ...(state.isOps
        ? [
            {
              label: `${OPS_EMAIL_NAME} <${OPS_EMAIL}>`,
              value: OPS_EMAIL,
              id: OPS_EMAIL,
            },
          ]
        : []),
      {
        label: `${user.name} <${user.email}>`,
        value: user.email,
        id: user.sub,
      },
    ];

    if (state.teamMembers.length) {
      state.teamMembers.map((member) => {
        let formattedMember = {
          label: `${member.name} <${member.email}>`,
          value: member.email,
          id: member.user_id,
        };
        senderOptions.push(formattedMember);
      });
    }

    return senderOptions;
  }, [user, state.teamMembers, state.isOps]);

  const templateOptions = useMemo(() => [
    {
      label: "Preliminary",
      options: [
        {
          label: "Preliminary - Domestic / Sales",
          value: "preliminary-domestic-sales",
          template: preliminaryDomesticSales,
        },
        ...(state.isOps ? [{
          label: "Preliminary - Domestic / Ops",
          value: "preliminary-domestic-ops",
          template: preliminaryDomesticOps,
        }] : []),
        {
          label: "Preliminary - International / Sales",
          value: "preliminary-international-sales",
          template: preliminaryInternationalSales,
        },
        ...(state.isOps ? [{
          label: "Preliminary - International / Ops",
          value: "preliminary-international-ops",
          template: preliminaryInternationalOps,
        }] : []),
      ],
    },
    {
      label: "Final",
      options: [
        {
          label: "Final - Sales",
          value: "final-sales",
          template: finalSales,
        },
        ...(state.isOps ? [{
          label: "Final - Ops",
          value: "final-ops",
          template: finalOps,
        }] : []),
      ],
    },
    {
      label: "Updated",
      options: [
        {
          label: "Updated",
          value: "updated",
          template: updated,
        },
      ],
    },
  ], [state.isOps]);

  const handleTemplateChange = useCallback(
    (template) => {
      if (state.bodyContentEditorUpdated) {
        dispatch({
          type: "fieldChanged",
          field: "showChangeEmailTemplateModal",
          value: true,
        });
        dispatch({
          type: "fieldChanged",
          field: "tmpTemplate",
          value: template,
        });
      } else {
        dispatch({
          type: "fieldChanged",
          field: "template",
          value: template,
        });
      }

      setUnsavedChanges({
        field: "template",
        value: template?.value !== state.initialFormValues.template?.value,
      })
    },
    [setUnsavedChanges, state.bodyContentEditorUpdated, state.initialFormValues.template?.value]
  );

  const handleCloseChangeEmailTemplateModal = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "showChangeEmailTemplateModal",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "tmpTemplate",
      value: null,
    });
  }, []);

  const handleConfirmChangeEmailTemplate = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "template",
      value: state.tmpTemplate,
    });
    handleCloseChangeEmailTemplateModal();
  }, [state.tmpTemplate, handleCloseChangeEmailTemplateModal]);

  const handleEditorChange = useCallback((editorState) => {
    setUnsavedChanges({
      field: 'bodyContentStateEditor',
      value:
        editorState?.getCurrentContent().getPlainText()
        !== state.initialFormValues.bodyContentStateEditor?.getCurrentContent().getPlainText(),
    });

    dispatch({
      type: "fieldChanged",
      field: "bodyContentStateEditor",
      value: editorState,
    });
    dispatch({
      type: "fieldChanged",
      field: "bodyContentEditorUpdated",
      value: true,
    });
  }, [setUnsavedChanges, state.initialFormValues.bodyContentStateEditor]);

  const recentActions = useMemo(() => {
    const lastActions = state.actionLog?.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    const lastPreliminaryEmail = lastActions.find(
      ({ action, data }) =>
        ["sendEmail", "sendEmailAndSyncToApp"].includes(action) &&
        data?.template?.includes("preliminary")
    );

    const lastFinalEmail = lastActions.find(
      ({ action, data }) =>
        ["sendEmail", "sendEmailAndSyncToApp"].includes(action) &&
        data?.template?.includes("final")
    );

    const lastDownload = lastActions.find(
      ({ action }) => action === "download"
    );

    return {
      preliminaryEmail: lastPreliminaryEmail
        ? moment(lastPreliminaryEmail.timestamp).format("M/D/YYYY - HHmm")
        : "N/A",
      finalEmail: lastFinalEmail
        ? moment(lastFinalEmail.timestamp).format("M/D/YYYY - HHmm")
        : "N/A",
      download: lastDownload
        ? moment(lastDownload.timestamp).format("M/D/YYYY - HHmm")
        : "N/A",
    };
  }, [state.actionLog]);

  useEffect(() => {
    setUnsavedChanges({
      field: "attachments",
      value: !!state.attachments?.length
    })
  }, [setUnsavedChanges, state.attachments]);

  const handleEdit = useCallback(() => {
    history.push(`/trips/${tripId}/itineraries/${itineraryId}`);
  }, [history, tripId, itineraryId])

  return (
    <>
      <div className="mt-30 mb-15">
        <PageHeader
          pageTitle="Preview Itinerary"
          breadcrumbs={[
            {link: '/trips', label: 'Trips'},
            {link: `/trips/${tripId}`, label: `Trip #${state.tripTag}`},
            {label: 'Preview Itinerary', isCurrentPage: true},
          ]}
          goBackLink={`/trips/${tripId}`}
          extraActions={
            <div className="d-flex flex-wrap flex-align-center gap-10">
              {!readOnly && (
                <button
                  onClick={handleEdit}
                  title="Edit Itinerary"
                  className="base-button icon-button evo-blue"
                >
                  <span className="material-icons">edit</span>
                </button>
              )}

              {superAdminOrAd && (
                <button
                  onClick={handlePDF}
                  title="Download PDF"
                  className="base-button icon-button evo-blue"
                >
                  <span className="material-icons">download</span>
                </button>
              )}
            </div>
          }
          noMargin
        />
      </div>

      <iframe
        className="contract-preview-container"
        id="contractPreviewIframe"
        sandbox="allow-same-origin"
        srcDoc={state.recordHTML}
      ></iframe>

      <Dropzone dispatch={dispatch} attachments={state.attachments} />

      {!readOnly && (
      <div id="sendTestEmail">
        <div className="form-group mt20">
          <label htmlFor="email">
            <strong>Send Test Email</strong>
          </label>

          <CreatableSelect
            className="email-select"
            classNamePrefix="select"
            placeholder="Enter Email Addresses"
            components={emailComponents}
            isClearable
            isMulti
            menuIsOpen={false}
            onChange={handleTestEmailChange}
            onInputChange={handleTestEmailInputChange}
            onKeyDown={handleTestEmailKeyDown}
            inputValue={state.testEmailInputValue}
            value={state.testEmails}
            name="testEmails"
            inputId="testEmails"
          />
        </div>

        <button
          className="base-button evo-blue w-100"
          type="button"
          onClick={sendTestEmail}
        >
          Send Test Email
        </button>
      </div>
      )}

      {!readOnly && (
      <div id="sendLiveEmail">
        <div id="emailHeaders">
          <h3>Email Headers</h3>
          <p>
            <strong>To: </strong>
            {state.emailOptions &&
              `${state.emailOptions?.firstName || ""} ${
                state.emailOptions?.lastName || ""
              }`}{" "}
            {state.emailOptions?.email &&
              `<${state.emailOptions?.email?.join(", ")}>`}
          </p>

          {ccList && (
            <p>
              <strong>Cc: </strong>
              {ccList}
            </p>
          )}

          {bccList && (
            <p>
              <strong>Bcc: </strong>
              {bccList}
            </p>
          )}

          <p>
            <strong>Subject: </strong>
            {state.emailOptions?.subject}
          </p>
        </div>

        <div className="form-group mt20">
          <label htmlFor="sendAs-select">
            <strong>Select a Sender</strong>
          </label>

          <Select
            key={`sendAs-${state.teamMembers.length}`}
            className="sendAs-select"
            classNamePrefix="select"
            name="sendAs-select"
            value={state.sendAs}
            options={senderOptions}
            placeholder="Select a Sender"
            onChange={(sender) => {
              setUnsavedChanges({
                field: "sendAs",
                value: sender.id !== state.initialFormValues.sendAs?.id,
              })

              dispatch({
                type: "fieldChanged",
                field: "sendAs",
                value: sender,
              });
            }}
          />
        </div>

        <div className="form-group mt20">
          <label htmlFor="template-select">
            <strong>Select a Template</strong>
          </label>

          <Select
            className="template-select"
            classNamePrefix="select"
            name="template-select"
            value={state.template}
            placeholder="Select a Template"
            onChange={handleTemplateChange}
            options={templateOptions}
            styles={{
              menu: (baseStyles) => ({
                ...baseStyles,
                zIndex: 2,
              }),
            }}
          />
        </div>

        <Editor
          editorState={state.bodyContentStateEditor}
          wrapperClassName="body-content-editor-wrapper"
          editorClassName="body-content-editor"
          onEditorStateChange={handleEditorChange}
        />

        <LoadingButton
          className="base-button evo-blue w-100 mb-20"
          type="button"
          onClick={sendLiveEmail}
          disabled={state.isFormLoading}
          isLoading={state.isEmailLoading}
        >
          Email Itinerary
        </LoadingButton>

      </div>
      )}

      <div>
        <h4 className="mt-20">Recent Actions</h4>

        <div>
          <div>App User?</div>
          <div>{state.appUser ? "Yes" : "No"}</div>
        </div>

        <div className="evo-table mt-20">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Emailed</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <b>Preliminary</b>
                </td>
                <td>{recentActions.preliminaryEmail}</td>
                <td rowSpan={2}>{recentActions.download}</td>
              </tr>
              <tr>
                <td>
                  <b>Final</b>
                </td>
                <td>{recentActions.finalEmail}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showChangeEmailTemplateModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseChangeEmailTemplateModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Change Email Template?</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            You've made changes to the current email body. Switching templates
            will permanently discard these edits — there’s no option to save
            them. Would you like to continue and load the new template?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseChangeEmailTemplateModal}
            >
              Cancel & Keep Current
            </button>

            <button
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleConfirmChangeEmailTemplate}
            >
              Confirm & Switch Template
            </button>
          </div>
        </div>
      </ReactModal>

      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>
    </>
  );
};

export default ItineraryPreviewContent;
